import { Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { updateMyPreferences } from "@/lib/api";
import { setTheme } from "@/lib/theme";
import { LANDING_PAGES, resolveLandingPath } from "@/lib/landingPages";
import { LANGUAGES, useI18n } from "@/i18n";
import { PillRail, Pill } from "@/components/common/Segmented";

// My defaults: landing page · theme · language. Saved on the user record, applied on every sign-in.
// variant "compact" (sidebar popover) stacks label-over-control; "rows" (profile page) puts label left, pill rail right.
export function UserPreferences({ showLanguageHint = false, variant = "compact" }) {
  const { user, refresh, can } = useAuth();
  const { t, lang } = useI18n();
  const theme = user?.theme || "dark";
  const landing = resolveLandingPath(user, can);
  const rows = variant === "rows";

  const save = async (patch, msg) => {
    try {
      await updateMyPreferences(patch);
      if (patch.theme) setTheme(patch.theme);
      await refresh();
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Could not save preference"));
    }
  };

  const Row = ({ label, children, testId }) => rows ? (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="text-[12.5px] font-medium text-slate-700">{label}</div>
      <div className="w-52 shrink-0">{children}</div>
    </div>
  ) : (
    <div data-testid={testId}>
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );

  const landingSelect = (
    <select
      value={landing}
      onChange={(e) => save({ landing_page: e.target.value }, t("Landing page: {page}", { page: t(LANDING_PAGES.find((p) => p.value === e.target.value)?.label) }))}
      className="h-8 w-full rounded-full border border-slate-200 bg-background px-3 text-xs font-medium outline-none focus:border-brand"
      data-testid="pref-landing-page"
    >
      {LANDING_PAGES.map((p) => <option key={p.value} value={p.value}>{t(p.label)}</option>)}
    </select>
  );
  const themePills = (
    <PillRail className="flex w-full flex-nowrap" testId="pref-theme">
      <Pill className="flex-1 justify-center" on={theme === "light"} onClick={() => theme !== "light" && save({ theme: "light" }, t("Default theme: Light"))} testId="pref-theme-light"><Sun className="w-3.5 h-3.5" /> {t("Light")}</Pill>
      <Pill className="flex-1 justify-center" on={theme === "dark"} onClick={() => theme !== "dark" && save({ theme: "dark" }, t("Default theme: Dark"))} testId="pref-theme-dark"><Moon className="w-3.5 h-3.5" /> {t("Dark")}</Pill>
    </PillRail>
  );
  const langPills = (
    <PillRail className="flex w-full flex-nowrap" testId="pref-language">
      {LANGUAGES.map((l) => (
        <Pill key={l.value} className="flex-1 justify-center" on={lang === l.value} testId={`pref-language-${l.value}`}
          onClick={() => lang !== l.value && save({ language: l.value }, l.value === "es" ? "Idioma: Español" : "Language: English")}>
          {l.label}
        </Pill>
      ))}
    </PillRail>
  );
  const splitStart = user?.split_start || "same";
  const splitRatio = user?.split_ratio || "50";
  const splitStartSelect = (
    <select
      value={splitStart}
      onChange={(e) => save({ split_start: e.target.value }, t("Split screen starts on: {page}", { page: e.target.value === "same" ? t("Same page") : t(LANDING_PAGES.find((p) => p.value === e.target.value)?.label || e.target.value) }))}
      className="h-8 w-full rounded-full border border-slate-200 bg-background px-3 text-xs font-medium outline-none focus:border-brand"
      data-testid="pref-split-start"
    >
      <option value="same">{t("Same page")}</option>
      {LANDING_PAGES.map((p) => <option key={p.value} value={p.value}>{t(p.label)}</option>)}
    </select>
  );
  const splitRatioPills = (
    <PillRail className="flex w-full flex-nowrap" testId="pref-split-ratio">
      {[["50", "50 / 50"], ["60", "60 / 40"], ["40", "40 / 60"]].map(([v, l]) => (
        <Pill key={v} className="flex-1 justify-center" on={splitRatio === v} testId={`pref-split-ratio-${v}`}
          onClick={() => splitRatio !== v && save({ split_ratio: v }, t("Split layout: {ratio}", { ratio: l }))}>
          {l}
        </Pill>
      ))}
      {!["50", "60", "40"].includes(splitRatio) && (
        <Pill className="flex-1 justify-center" on testId="pref-split-ratio-custom" onClick={() => {}}>
          {`${splitRatio} / ${100 - Number(splitRatio)}`}
        </Pill>
      )}
    </PillRail>
  );

  return (
    <div className={rows ? "" : "pt-2 border-t border-slate-100 space-y-2"} data-testid="user-preferences">
      {!rows && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t("My defaults")}</div>}
      <Row label={t("Landing page")} testId="pref-row-landing">{landingSelect}</Row>
      <Row label={rows ? t("Dark mode") : t("Theme on sign-in")} testId="pref-row-theme">{themePills}</Row>
      <Row label={t("Language")} testId="pref-row-language">{langPills}</Row>
      {rows && <Row label={t("Split screen starts on")} testId="pref-row-split-start">{splitStartSelect}</Row>}
      {rows && <Row label={t("Split layout (left / right)")} testId="pref-row-split-ratio">{splitRatioPills}</Row>}
      {showLanguageHint && lang === "es" && (
        <p className="text-[10px] text-slate-500 pt-2" data-testid="pref-language-hint">
          {t("Notes you type in Spanish are saved in English for the office; notes written in English are shown to you in Spanish.")}
        </p>
      )}
    </div>
  );
}

export default UserPreferences;
